import { useState } from 'react';
import { useAsyncFn } from 'react-use';

import { Anchor, Box, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconLockClosedOutline } from '@zillow/constellation-icons';

import errorMessages from '@common/constants/errorMessages';
import { validateAllInputs } from '../validation';
import faqs from '@common/constants/faqs';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import userPersonas from '@common/constants/userPersonas';
import Modal from '@common/components/ConstellationV10Modal';
import PhoneVerificationInput from '@rental-manager/components/PhoneVerificationInput';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  confirmVerificationCode: ({ verificationCode }) => Promise<any>;
  currentProperty: ZrmClickstreamListing;
  fromDashboard: boolean;
  onBack: () => void;
  onClose: () => void;
  phoneNumber: string;
  sendVerificationCode: ({ verificationMethod }) => Promise<any>;
  trackValidationError: ({ errors }) => void;
  user: ZrmClickstreamUser;
  verificationMethod: string;
}

const SubmissionPage = (props: Props) => {
  const {
    confirmVerificationCode,
    currentProperty,
    fromDashboard,
    onBack,
    onClose,
    phoneNumber,
    sendVerificationCode,
    trackValidationError,
    user,
    verificationMethod,
  } = props;
  const [verificationCode, setVerificationCode] = useState({
    value: '',
    error: '',
  });
  const [resendStatus, setResendStatus] = useState<string | null>(null);

  const resendVerificationCode = async (): Promise<any> => {
    setResendStatus('init');

    const response = await sendVerificationCode({ verificationMethod });

    if (response && response.error) {
      setResendStatus('fail');
    } else {
      setResendStatus('success');
    }
  };

  const [{ loading: isLoading }, onSubmit] = useAsyncFn(async () => {
    if (!validateAllInputs(verificationCode, setVerificationCode, trackValidationError)) {
      return;
    }

    const response = await confirmVerificationCode({ verificationCode: verificationCode.value });

    if (response && response.error) {
      setVerificationCode({
        value: verificationCode.value,
        error: response.tooManyAttempts
          ? errorMessages.PHONE_VERIFICATION_TOO_MANY_ATTEMPTS
          : errorMessages.PHONE_VERIFICATION_INCORRECT,
      });
    } else {
      if (!fromDashboard) {
        IreneOnboardingCSEvents.emitContinueFromPhoneVerificationPageEvent({
          currentProperty,
          user,
        });
      }
    }
  }, [verificationCode, confirmVerificationCode, trackValidationError, fromDashboard, currentProperty, user]);

  const renderResendSection = () => {
    if (resendStatus === 'success') {
      return 'Code sent!';
    } else if (resendStatus === 'fail') {
      return 'Sorry, we were unable to re-send the code.';
    }
    return <Anchor onClick={resendVerificationCode}>Resend code</Anchor>;
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={onBack}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            {fromDashboard ? 'Edit deposit account' : 'Payment setup'}
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.md) !important',
          }}
        >
          <Paragraph textStyle="body-lg-bold">Enter your confirmation code</Paragraph>

          <Paragraph>We&apos;ll {verificationMethod === 'sms' ? 'text' : 'call'} you with your code soon.</Paragraph>

          <Paragraph className="fs-exclude">Phone: {phoneNumber}</Paragraph>

          <PhoneVerificationInput
            setVerificationCode={(val) => setVerificationCode({ value: val, error: '' })}
            error={verificationCode.error}
          />
          <Paragraph>{renderResendSection()}</Paragraph>
          <FaqComponent title={faqs.PHONE_VERIFICATION.title} faqs={faqs.PHONE_VERIFICATION.body} />
        </Box>
      }
      footer={
        <Box
          css={{
            padding: 'token(spacing.sm) !important',
          }}
        >
          <DebouncedButton icon={<IconLockClosedOutline />} loading={isLoading} onClick={onSubmit}>
            Submit
          </DebouncedButton>
        </Box>
      }
    />
  );
};

export default SubmissionPage;
